---
id: iac-migration-planner
title: IaC Migration Planner
category: iac
difficulty: advanced
tags:
  - migration
  - terraform
  - import
  - cloudformation
  - arm
  - planning
description: >
  Plans migration of existing cloud resources to Terraform, generating
  import blocks and module structure.
whenToUse: >
  When adopting IaC for existing manually-created infrastructure or
  migrating from CloudFormation/ARM templates.
variables:
  - name: source
    desc: Current IaC tool or "manual" for console-created resources
  - name: cloud
    desc: Cloud provider
  - name: resource_list
    desc: List of resources with their IDs/ARNs
  - name: tf_version
    desc: Target Terraform version
  - name: zero_downtime
    desc: Whether zero-downtime is required (yes/no)
  - name: experience_level
    desc: Team experience with Terraform (beginner/intermediate/advanced)
tips:
  - Always start with network/foundation resources — everything else depends on them.
  - Use terraform plan after each import to verify the state matches reality.
  - Consider using terraform import -generate-config (TF 1.5+) to auto-generate HCL.
---

You are an infrastructure migration specialist. Plan the migration of existing resources to Terraform.

## Current State
- Source: {{source}} (manual/CloudFormation/ARM/Pulumi)
- Cloud: {{cloud}}
- Resources to migrate:
```
{{resource_list}}
```

## Constraints
- Terraform version: {{tf_version}}
- Zero-downtime requirement: {{zero_downtime}}
- Team Terraform experience: {{experience_level}}

## Task
Create a phased migration plan:
1. Propose module structure (directory layout)
2. Generate import blocks for each resource
3. Define dependency order (what must be imported first)
4. Identify resources that can't be imported (must recreate)
5. Suggest state verification steps after each phase

## Output Format (JSON)
{
  "module_structure": {
    "directory": "tree representation"
  },
  "phases": [
    {
      "name": "Phase N — description",
      "resources": ["resource list"],
      "import_blocks": "HCL import blocks",
      "verification": "how to verify this phase",
      "risk_level": "low | medium | high",
      "rollback_plan": "how to rollback if needed"
    }
  ],
  "cannot_import": [
    { "resource": "name", "reason": "why", "alternative": "what to do instead" }
  ],
  "estimated_effort": "string"
}

<!-- EXAMPLE_OUTPUT -->

{
  "phases": [
    {
      "name": "Phase 1 — Network Foundation",
      "resources": ["VPC", "Subnets", "Route Tables", "NAT Gateway"],
      "import_blocks": "import { to = aws_vpc.main\n  id = \"vpc-abc123\" }",
      "verification": "terraform plan shows no changes for imported resources",
      "risk_level": "low",
      "rollback_plan": "terraform state rm for imported resources"
    }
  ]
}
